<template>
    <div class="media-preview">
        <!-- Image Preview -->
        <div v-if="mediaType === 'IMAGE'" class="image-preview">
            <img :src="previewUrl" :alt="media.originalFilename" class="img-fluid" />
        </div>

        <!-- Video Preview -->
        <div v-else-if="mediaType === 'VIDEO'" class="video-preview">
            <video controls class="w-100">
                <source :src="previewUrl" :type="media.mimeType">
                Your browser does not support the video tag.
            </video>
        </div>

        <!-- PDF Preview -->
        <div v-else-if="mediaType === 'DOCUMENT' && isPdf" class="document-preview">
            <iframe :src="previewUrl" class="pdf-iframe" frameborder="0"></iframe>
            <div class="preview-actions mt-2">
                <b-button variant="primary" :href="previewUrl" download target="_blank">
                    <i class="fal fa-download"></i> Download PDF
                </b-button>
            </div>
        </div>

        <!-- Generic Document Preview -->
        <div v-else-if="mediaType === 'DOCUMENT'" class="document-preview generic">
            <div class="document-icon">
                <i :class="getDocumentIcon()" class="fa-5x text-muted"></i>
            </div>
            <div class="document-info">
                <h5>{{ media.originalFilename }}</h5>
                <p class="text-muted">{{ formatFileSize(media.fileSize) }}</p>
                <p class="text-muted" v-if="media.description">{{ media.description }}</p>
            </div>
            <div class="preview-actions mt-3">
                <b-button variant="primary" :href="previewUrl" download target="_blank">
                    <i class="fal fa-download"></i> Download
                </b-button>
            </div>
        </div>

        <!-- Fallback -->
        <div v-else class="preview-fallback">
            <i class="fal fa-file fa-5x text-muted"></i>
            <p class="mt-3">Preview not available</p>
            <b-button variant="outline-primary" :href="previewUrl" download target="_blank">
                <i class="fal fa-download"></i> Download
            </b-button>
        </div>

        <!-- Media Info Footer -->
        <div class="media-info-footer mt-3" v-if="showInfo">
            <div class="info-row">
                <span class="label">Filename:</span>
                <span class="value">{{ media.originalFilename }}</span>
            </div>
            <div class="info-row" v-if="media.fileSize">
                <span class="label">Size:</span>
                <span class="value">{{ formatFileSize(media.fileSize) }}</span>
            </div>
            <div class="info-row" v-if="media.mimeType">
                <span class="label">Type:</span>
                <span class="value">{{ media.mimeType }}</span>
            </div>
            <div class="info-row" v-if="media.createdAt">
                <span class="label">Uploaded:</span>
                <span class="value">{{ formatDate(media.createdAt) }}</span>
            </div>
            <div class="info-row" v-if="media.tags">
                <span class="label">Tags:</span>
                <span class="value">{{ media.tags }}</span>
            </div>
            <div class="info-row" v-if="media.description">
                <span class="label">Description:</span>
                <span class="value">{{ media.description }}</span>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'MediaPreview',
        props: {
            media: {
                type: Object,
                required: true
            },
            showInfo: {
                type: Boolean,
                default: true
            }
        },
        computed: {
            mediaType() {
                return this.media.mediaType || 'DOCUMENT';
            },
            isPdf() {
                return this.media.mimeType === 'application/pdf' ||
                       (this.media.filename && this.media.filename.toLowerCase().endsWith('.pdf'));
            },
            previewUrl() {
                // Use the public file endpoint for serving files
                return this.fileResourceUrl(this.media.filename);
            }
        },
        methods: {
            getDocumentIcon() {
                const mimeType = this.media.mimeType || '';
                const filename = this.media.filename || '';

                if (mimeType.includes('pdf') || filename.endsWith('.pdf')) {
                    return 'fal fa-file-pdf';
                } else if (mimeType.includes('word') || filename.match(/\.(doc|docx)$/)) {
                    return 'fal fa-file-word';
                } else if (mimeType.includes('excel') || filename.match(/\.(xls|xlsx)$/)) {
                    return 'fal fa-file-excel';
                } else if (mimeType.includes('powerpoint') || filename.match(/\.(ppt|pptx)$/)) {
                    return 'fal fa-file-powerpoint';
                }
                return 'fal fa-file-alt';
            },

            formatFileSize(bytes) {
                if (!bytes) return 'Unknown';
                if (bytes < 1024) return bytes + ' B';
                if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
            },

            formatDate(dateString) {
                if (!dateString) return '';
                const date = new Date(dateString);
                return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
            }
        }
    }
</script>

<style lang="scss" scoped>
    .media-preview {
        .image-preview {
            text-align: center;

            img {
                max-height: 500px;
                object-fit: contain;
            }
        }

        .video-preview {
            video {
                max-height: 500px;
            }
        }

        .document-preview {
            &.generic {
                text-align: center;
                padding: 2rem;

                .document-icon {
                    margin-bottom: 1rem;
                }

                .document-info {
                    h5 {
                        margin-bottom: 0.5rem;
                        word-break: break-word;
                    }
                }
            }

            .pdf-iframe {
                width: 100%;
                height: 600px;
                border: 1px solid #dee2e6;
            }

            .preview-actions {
                text-align: center;
            }
        }

        .preview-fallback {
            text-align: center;
            padding: 3rem;
        }

        .media-info-footer {
            border-top: 1px solid #dee2e6;
            padding-top: 1rem;

            .info-row {
                display: flex;
                margin-bottom: 0.5rem;

                .label {
                    font-weight: 600;
                    min-width: 100px;
                    color: #6c757d;
                }

                .value {
                    flex: 1;
                    word-break: break-word;
                }
            }
        }
    }
</style>
